/**
 * Form validation messages as translation keys (principle 8).
 *
 * Zod produces English sentences. Rendering one would put an untranslated, developer-flavoured
 * string in front of a Norwegian user, so nothing here ever renders `issue.message`: a resolver
 * turns each issue into a `validation.*` key plus its parameters, and `translateValidation()`
 * turns that back into a sentence in the active language.
 *
 * The encoding is `key::{json}` — one string, because that is all react-hook-form carries.
 */
import type { FieldErrors, FieldValues, Resolver } from 'react-hook-form';
import type { TFunction } from 'i18next';
import type { z } from 'zod';

import type { TKey } from '@/i18n/types';

const SEPARATOR = '::';

export function validationMessage(key: TKey, params?: Record<string, unknown>): string {
  return params === undefined ? key : `${key}${SEPARATOR}${JSON.stringify(params)}`;
}

export function translateValidation(t: TFunction, message: string | undefined): string | undefined {
  if (!message) return undefined;
  const index = message.indexOf(SEPARATOR);
  if (index === -1) return t(message as TKey);
  const key = message.slice(0, index) as TKey;
  try {
    const params = JSON.parse(message.slice(index + SEPARATOR.length)) as Record<string, unknown>;
    return t(key, params);
  } catch {
    return t(key);
  }
}

function valueAtPath(values: unknown, path: readonly PropertyKey[]): unknown {
  let current: unknown = values;
  for (const segment of path) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<PropertyKey, unknown>)[segment];
  }
  return current;
}

function isEmpty(value: unknown): boolean {
  return value === undefined || value === null || value === '' || value === false;
}

function messageForIssue(issue: z.core.$ZodIssue, values: unknown): string {
  if (isEmpty(valueAtPath(values, issue.path))) {
    return validationMessage(
      typeof valueAtPath(values, issue.path) === 'boolean'
        ? 'validation.mustAccept'
        : 'validation.required',
    );
  }
  switch (issue.code) {
    case 'too_small':
      /*
       * A list that is too short is not a string that is too short.
       *
       * Submitting the new-thread dialog with nobody picked rendered "Må være minst 1 tegn."
       * under the *Deltakere* field — a character count, for a list of people. The `isEmpty`
       * guard above catches `undefined | null | '' | false` and an empty array is none of those,
       * so every `too_small` fell through to the string message.
       *
       * Zod reports what it was measuring, so ask it rather than inspecting the value: `origin`
       * is `'array'` for a list, `'string'` for a string, and this stays right for the next
       * multi-select somebody builds without them having to find this line.
       */
      /*
       * And a NUMBER that is too small is neither of the other two.
       *
       * `editions.year` is `z.number().int().min(1900).max(2200)` — the first numeric field in
       * the product — and typing 12 into it rendered "Må være minst 1900 tegn." under a year.
       * A character count, for a number. Same failure as the array case above and the same fix:
       * ask Zod what it was measuring rather than guessing from the value.
       */
      return validationMessage(
        issue.origin === 'array'
          ? 'validation.tooFewItems'
          : issue.origin === 'number'
            ? 'validation.tooSmallNumber'
            : 'validation.tooSmall',
        { min: Number(issue.minimum) },
      );
    case 'too_big':
      return validationMessage(
        issue.origin === 'number' ? 'validation.tooBigNumber' : 'validation.tooBig',
        { max: Number(issue.maximum) },
      );
    case 'invalid_format':
      return issue.format === 'email'
        ? validationMessage('validation.email')
        : validationMessage('validation.invalidFormat');
    case 'invalid_type':
      return validationMessage('validation.invalidType');
    case 'invalid_value':
      return validationMessage('validation.mustAccept');
    case 'custom':
      /*
       * `urlInputSchema` REISER SIN EGEN KODE (#16c).
       *
       * Den normaliserer i en `transform`, og en `transform` kan ikke rapportere `invalid_format`
       * — alt som feiler rundt den er `custom`. Uten denne linja ville hvert URL-felt i produktet
       * svart «Verdien er ikke gyldig», som ikke sier hva man skal gjøre. `invalid_url` er
       * strengen skjemaet setter, og den er den samme i nettleseren og i API-et.
       */
      return issue.message === 'invalid_url'
        ? validationMessage('validation.url')
        : validationMessage('validation.custom');
    default:
      return validationMessage('validation.custom');
  }
}

function assignError(
  target: Record<string, unknown>,
  path: readonly PropertyKey[],
  message: string,
): void {
  const key = String(path.at(0) ?? 'root');
  if (path.length <= 1) {
    target[key] ??= { type: 'validation', message };
    return;
  }
  const next = (target[key] as Record<string, unknown> | undefined) ?? {};
  target[key] = next;
  assignError(next, path.slice(1), message);
}

/**
 * A resolver over the **shared request schema** — the very schema NestJS validates with, so the
 * browser and the API agree on what "valid" means (principle 7).
 */
export function i18nZodResolver<TSchema extends z.ZodType, TValues extends FieldValues>(
  schema: TSchema,
): Resolver<TValues> {
  const resolver: Resolver<TValues> = (values) => {
    const result = schema.safeParse(values);
    if (result.success) {
      return { values: result.data as TValues, errors: {} };
    }
    const errors: Record<string, unknown> = {};
    for (const issue of result.error.issues) {
      assignError(
        errors,
        issue.path.length > 0 ? issue.path : ['root'],
        messageForIssue(issue, values),
      );
    }
    return { values: {}, errors: errors as FieldErrors<TValues> };
  };
  return resolver;
}
